import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { CreditCard, CheckCircle, XCircle, TrendingUp, Calendar, Shield, Clock, RefreshCw, Info, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';

export default function CreditReporting() {
  const { user } = useAuth();
  const [consent, setConsent] = useState(null);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [consentRes, historyRes, summaryRes] = await Promise.all([
        api.get('/tenant/credit-reporting/consent'),
        api.get('/tenant/credit-reporting/history'),
        api.get('/tenant/credit-reporting/summary'),
      ]);
      setConsent(consentRes.data);
      setHistory(historyRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConsentToggle = async (enabled) => {
    try {
      await api.post('/tenant/credit-reporting/consent', {
        consent_given: enabled,
        consent_scope: { payment_history: true, arrears: enabled },
        privacy_notice_version: '1.0',
      });
      fetchData();
    } catch (error) {
      console.error('Error updating consent:', error);
    }
  };

  const handleWithdrawConsent = async () => {
    try {
      await api.post('/tenant/credit-reporting/consent/withdraw', {
        withdrawal_reason: withdrawReason,
      });
      setWithdrawDialogOpen(false);
      setWithdrawReason('');
      fetchData();
    } catch (error) {
      console.error('Error withdrawing consent:', error);
    }
  };

  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case 'on_time':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'late':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'missed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'on_time': return 'bg-green-100 text-green-800';
      case 'late': return 'bg-yellow-100 text-yellow-800';
      case 'missed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Credit Reporting</h1>
          <p className="text-gray-500">Manage your rent payment reporting to credit reference agencies</p>
        </div>

        {/* Consent Card */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Rent Payment Reporting</CardTitle>
                  <CardDescription>Report your rent payments to build your credit history</CardDescription>
                </div>
              </div>
              <Switch
                checked={consent?.consent_given || false}
                onCheckedChange={handleConsentToggle}
                disabled={consent?.withdrawn}
              />
            </div>
          </CardHeader>
          <CardContent>
            {consent?.consent_given && !consent?.withdrawn ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Credit Reporting Active</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">
                    Your on-time rent payments are being reported to credit reference agencies, helping build your credit score.
                  </p>
                  {consent?.consent_date && (
                    <p className="text-xs text-green-500 mt-2">
                      Consent given on {new Date(consent.consent_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={() => setWithdrawDialogOpen(true)}>
                    Withdraw Consent
                  </Button>
                </div>
              </div>
            ) : consent?.withdrawn ? (
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-600">
                  <XCircle className="w-5 h-5" />
                  <span className="font-medium">Consent Withdrawn</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  You withdrew your consent on {new Date(consent.withdrawn_at).toLocaleDateString()}.
                  You can re-enable reporting at any time.
                </p>
              </div>
            ) : (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-700">Why report rent payments?</h4>
                    <ul className="text-sm text-blue-600 mt-2 space-y-1">
                      <li>• Build your credit history with regular on-time payments</li>
                      <li>• Improve your chances of mortgage approval</li>
                      <li>• Demonstrate financial responsibility to lenders</li>
                      <li>• No cost to you - it&apos;s completely free</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Stats */}
        {summary && summary.reporting_active && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Total Reports</div>
                    <div className="text-2xl font-bold">{summary.total_reports}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">On Time</div>
                    <div className="text-2xl font-bold text-green-600">{summary.on_time_payments}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Late</div>
                    <div className="text-2xl font-bold text-yellow-600">{summary.late_payments}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">On-Time Rate</div>
                    <div className="text-2xl font-bold text-purple-600">{summary.on_time_percentage}%</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reporting History */}
        <Card>
          <CardHeader>
            <CardTitle>Reporting History</CardTitle>
            <CardDescription>Your rent payment reports sent to credit agencies</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Expected</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Days Late</TableHead>
                  <TableHead>Reported</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map(report => (
                  <TableRow key={report.id}>
                    <TableCell>
                      {report.report_period_start} to {report.report_period_end}
                    </TableCell>
                    <TableCell>{formatCurrency(report.expected_amount)}</TableCell>
                    <TableCell>{formatCurrency(report.payment_amount)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getPaymentStatusIcon(report.payment_status)}
                        <Badge className={getPaymentStatusColor(report.payment_status)}>
                          {report.payment_status.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      {report.days_late > 0 ? (
                        <span className="text-red-500">{report.days_late} days</span>
                      ) : (
                        <span className="text-green-500">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        report.status === 'submitted' ? 'border-green-300' :
                        report.status === 'pending' ? 'border-yellow-300' : ''
                      }>
                        {report.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {history.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                {consent?.consent_given ? (
                  <>
                    <h3 className="font-medium">No Reports Yet</h3>
                    <p className="text-sm mt-1">Reports are generated at the end of each rental period.</p>
                  </>
                ) : (
                  <>
                    <h3 className="font-medium">Enable Credit Reporting</h3>
                    <p className="text-sm mt-1">Turn on consent to start building your credit history.</p>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Withdraw Dialog */}
        <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Withdraw Consent</DialogTitle>
              <DialogDescription>
                Are you sure you want to stop rent payment reporting? This will stop building your credit history.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-700">
                    <p className="font-medium">Please note:</p>
                    <ul className="mt-1 space-y-1">
                      <li>• Future payments won&apos;t be reported</li>
                      <li>• Historical reports will remain on record</li>
                      <li>• You can re-enable at any time</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Reason for withdrawal (optional)</Label>
                <Textarea
                  value={withdrawReason}
                  onChange={(e) => setWithdrawReason(e.target.value)}
                  placeholder="Let us know why you&apos;re withdrawing consent..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setWithdrawDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleWithdrawConsent}>Withdraw Consent</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
